import { aggregateValuesByPeriod } from '@/types'
import { listPeriods, type Timeframe } from '../../functions-src/lib/calendar'

// Maximum number of forecast periods to show ahead
const MAX_FORECAST_PERIODS = 2

export interface MetricChartPoint {
  date: string
  value: number | null
  timestamp: number
  count: number | undefined
  isForecast: boolean
}

// Transform metric values for chart - shows complete timeline with forecast for missing periods
export function transformChartData(
  values: { value: number; recorded_at: number }[],
  timeframe?: Timeframe | null,
  periodCount: number = 8,
  now: number = Math.floor(Date.now() / 1000)
): MetricChartPoint[] {
  // Timeline (past periods + current period), oldest first
  const timeline = listPeriods(now, timeframe ?? 'monthly', periodCount)

  // Aggregate actual values by period; the aggregate's recorded_at is the period start
  const aggregated = aggregateValuesByPeriod(values, timeframe)
  const periodStartToKey = new Map(timeline.map(p => [p.start, p.key]))
  const valuesByPeriodKey = new Map<string, { value: number; count: number }>()
  aggregated.forEach(v => {
    const key = periodStartToKey.get(v.recorded_at)
    if (key !== undefined) valuesByPeriodKey.set(key, { value: v.value, count: v.count })
  })

  // Find the index of last period with actual data
  let lastActualIdx = -1
  timeline.forEach((period, idx) => {
    if (valuesByPeriodKey.has(period.key)) lastActualIdx = idx
  })

  // Mark periods after last actual as forecast (up to MAX_FORECAST_PERIODS)
  return timeline.map((period, idx) => {
    const data = valuesByPeriodKey.get(period.key)
    const periodsAfterLastActual = idx - lastActualIdx
    const isForecast = data === undefined &&
      lastActualIdx >= 0 &&
      periodsAfterLastActual > 0 &&
      periodsAfterLastActual <= MAX_FORECAST_PERIODS

    return {
      date: period.label,
      value: data?.value ?? null,
      timestamp: period.start,
      count: data?.count,
      isForecast,
    }
  })
}
